import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PageParams } from '../../../../../core/models/page-params.model';
import { IPaginatorProperties } from '../../../../../core/models/paginator.interface';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import {
    ICarrierItem,
    IPriceListFilter, IProviderItem,
    IService, IShipmentPriceAction,
    ISubCarrierPrice,
    ISubCarrierPriceError,
    ISubService, ITypeActivity, ITypeActivityPrice, ITypeActivityPriceError,
    IVendor,
} from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';

@Component({
    selector: 'eci-fulfilment',
    templateUrl: './fulfilment.component.html',
    styleUrls: [ './fulfilment.component.scss' ]
})
export class FulfilmentComponent implements OnInit {

    public form: FormGroup;

    public priceList: ITypeActivityPriceError[] = [];
    public showLoader = false;
    public currentSort: Sort = {active: 'createdAt', direction: 'desc'};
    public pageParams: PageParams = new PageParams();

    @Input() public vendors: IPaginatorProperties<IVendor> = {items: [], meta: undefined};
    @Input() public services: IPaginatorProperties<IService> = {items: [], meta: undefined};
    @Input() public carriers: IPaginatorProperties<IProviderItem> = {items: [], meta: undefined};
    @Input() public subServices: IPaginatorProperties<ISubService> = {items: [], meta: undefined};
    @Input() public files: IPaginatorProperties<{ id: number; fileName: string }> = {items: [], meta: undefined};

    private searchValue: string;
    private filters: IPriceListFilter;

    public dataSource: MatTableDataSource<ITypeActivityPriceError>;
    public displayedColumns: string[] = [
        'fileName',
        'vendor',
        'service',
        'subService',
        'carrierProvider',
        'typeName',
        'subTypeName',
        'description',
        'activityUnitName',
        'costRates',
        'margin',
        'sellPrice',
        'currency',
        'maxLength',
        'maxWidth',
        'maxHeight',
        'dimensionsUnitsOfMeasure',
        'maxWeight',
        'weightUnitsOfMeasure',
        'maxVolume',
        'volumeUnitsOfMeasure',
        'errorReason',
        'action',
    ];

    public typeActivityList: ITypeActivity[];
    public subTypeActivityList: ITypeActivity[];
    public activityUnitList: ITypeActivity[];

    public constructor(private userSvc: UserService,
                       public router: Router,
                       private notify: NotificationService,
                       // tslint:disable-next-line:no-parameter-properties
                       private shipmentPriceSvc: ShipmentPriceService) {
    }

    public ngOnInit(): void {
        this.getList();

        this.getTypeActivityList();
        this.getActivityUnitOfMeasuresList();
        this.getSubTypeActivityList();
    }

    private getList(page: number = this.pageParams.pageIndex + 1 || 1,
                    limit: number = this.pageParams.pageSize,
                    q: string = this.searchValue || '',
                    orderField: string = this.currentSort?.active || '',
                    orderDir: string = this.currentSort?.direction.toUpperCase() || ''): void {
        this.showLoader = true;
        this.shipmentPriceSvc.get<IPaginatorProperties<ITypeActivityPriceError>>(
            'type-activity-price-errors', page, limit, q, this.filters, orderField, orderDir)
            .subscribe((data) => {
                if (!data) {
                    return;
                }
                this.priceList = data.items;

                this.dataSource = new MatTableDataSource<ITypeActivityPriceError>(this.priceList);
                this.pageParams.pageSize = data.meta.itemsPerPage;
                this.pageParams.pageIndex = data.meta.currentPage - 1;
                this.pageParams.length = data.meta.totalItems;
            }, (err) => {
                this.notify.showError(err.error.message);
            }, () => {
                this.showLoader = false;
            });
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getList();
    }

    public sortData(sort: Sort): void {
        const data = this.dataSource;
        if (!sort.active || sort.direction === '') {
            this.dataSource = data;
            return;
        }
        this.currentSort = sort;
        this.getList();
    }

    public onFilterUpdate(value: IPriceListFilter): void {
        this.searchValue = value.search;
        this.filters = value;
        this.getList();
    }

    public update(data: IShipmentPriceAction): void {
        this.showLoader = true;
        this.shipmentPriceSvc.update<ITypeActivityPriceError>('type-activity-price-errors', data.element.id, data.form)
            .subscribe((p) => {
                if (!p) {
                    this.notify.showError(`Something went wrong`);
                }
                this.notify.showSuccessMessage(`Updated successfully`);
                data.onSuccess();
                this.removeData(data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
            }, () => {
                this.showLoader = false;
            });
    }

    public delete(element: { id: number; index: number }): void {
        this.showLoader = true;
        this.shipmentPriceSvc.delete('type-activity-price-errors', element.id)
            .subscribe((p) => {
                if (!p) {
                    this.notify.showError(`Something went wrong`);
                }
                this.notify.showSuccessMessage(`Deleted successfully`);
                this.removeData(element.index);
            }, (err) => {
                this.notify.showError(err.error.message);
            }, (): void => {
                this.showLoader = false;
            });
    }

    public removeData(index: number): void {
        this.priceList.splice(index, 1);
        this.dataSource.data = this.priceList;
    }

    private getTypeActivityList() {
        this.shipmentPriceSvc.get('type-activities', undefined, 1000, undefined,
            {isSubType: false}).subscribe((data: any) => {
            this.typeActivityList = data?.items;
        });
    }

    private getSubTypeActivityList() {
        this.shipmentPriceSvc.get('type-activities', undefined, 1000, undefined,
            {isSubType: true}).subscribe((data: any) => {
            this.subTypeActivityList = data?.items;
        });
    }

    private getActivityUnitOfMeasuresList() {
        this.shipmentPriceSvc.get('activity-unit-of-measures', undefined, 1000).subscribe((data: any) => {
            this.activityUnitList = data?.items;
        });
    }
    //
    // public getSubTypeActivityListByParentId(parentId: number) {
    //     this.shipmentPriceService.get(`type-activities/${parentId}/sub-types`, undefined, undefined, undefined,
    //         {isSubType: true, parentId}).subscribe((data: any) => {
    //         this.theSubTypeActivityByParentIdList = data?.items;
    //     });
    // }
}
